import * as React from "react";
import { Account } from "@/types";
import { ExpressUiInfoContext } from "@/store/ExpressUiInfo";
import { dealWithQuilter } from "@/helpers/account-helpers";
import {
  ExpressStoreKey,
  getExpressSectionSourceDataByKey,
  putExpressSectionDataByKey,
} from "@/pages/ExpressForm/express-data";
import { randvalue } from "@/utils/Functions";
import { SPContext } from "@/store/SPProvider";

export enum MultiAccountUi {
  Form,
  List,
}

export enum StatusAccount {
  Init,
  Done,
}

export interface MultiAccountState {
  current: Account | undefined;
  accounts: Account[];
  account_state: Map<string, StatusAccount>;
  view: MultiAccountUi;
}

export type MAS = MultiAccountState;
const STORAGE_KEY = ExpressStoreKey.MultiAccount;

export const useMultiAccount = (): [
  MAS,
  React.Dispatch<React.SetStateAction<MAS>>,
  {
    onBtnClickBack: () => void;
    multiAccountRemove: (id: string | undefined) => void;
    onBtnClickAdd: ({ target }: { target: Account }) => void;
  }
] => {
  const [state, setState] = React.useState<MAS>({
    current: undefined,
    accounts: [],
    account_state: new Map<string, StatusAccount>(),
    view: MultiAccountUi.List,
  });
  const {
    state: { quilter },
    dispatch,
  } = React.useContext(ExpressUiInfoContext);
  const { state: sp } = React.useContext(SPContext);

  React.useEffect(() => {
    const localData = getExpressSectionSourceDataByKey(STORAGE_KEY);
    if (localData) {
      setState((prev) => {
        localData.forEach((a: Account) =>
          prev.account_state.set(a.id as string, StatusAccount.Done)
        );
        return {
          ...prev,
          accounts: localData,
          account_state: prev.account_state,
        };
      });
    }
  }, []);

  React.useEffect(() => {
    putExpressSectionDataByKey(STORAGE_KEY, { source: state.accounts, target: state.accounts });
    dealWithQuilter(quilter.isQuilter, state.accounts, dispatch);
  }, [state.accounts, quilter.isQuilter, dispatch]);

  const onBtnClickBack = React.useCallback(() => {
    setState((prev: MAS) => {
      if (prev.current && isDone(prev)) {
        return {
          ...prev,
          view: MultiAccountUi.List,
          accounts: [...prev.accounts, prev.current],
          current: undefined,
          accountDataForExistingForm: undefined,
        };
      } else {
        return {
          ...prev,
          view: MultiAccountUi.List,
          current: undefined,
          accountDataForExistingForm: undefined,
        };
      }
    });
  }, [setState]);

  const multiAccountRemove = React.useCallback((id: string | undefined) => {
    setState((s: MAS) => {
      const updatedAccounts = s.accounts.filter(({ id: aid }: Account) => id !== aid);
      return {
        ...s,
        accounts: updatedAccounts,
      };
    });
  }, []);

  const onBtnClickAdd = React.useCallback(
    ({ target }: { source?: Account; target?: Account }) => {
      // dnr save account only if targetreturns
      if (!target) return;
      let account = { ...target, id: randvalue(), companyName: sp.companyName };

      setState((prev: MAS) => {
        prev.account_state.set(account.id, StatusAccount.Done);

        return {
          ...prev,
          view: MultiAccountUi.List,
          current: undefined,
          accounts: [...prev.accounts, account],
          account_state: prev.account_state,
        };
      });
    },
    [setState]
  );
  const helpers = { onBtnClickBack, multiAccountRemove, onBtnClickAdd };

  return [state, setState, helpers];
};

export function isDone(state: MAS): boolean {
  if (!state?.current?.id) return false;
  return state.account_state.get(state?.current?.id) === StatusAccount.Done;
}
